import React, { useState, useEffect } from 'react';
import { Cookie, X, ChevronDown, ChevronUp, Shield, BarChart3, Target, Sparkles } from 'lucide-react';
import { consentManager, ConsentCategory, ConsentState } from '../utils/consentManager';

const COOKIE_CONSENT_KEY = 'flashread_cookie_consent';

export const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [consentState, setConsentState] = useState<ConsentState>(consentManager.getState());

  useEffect(() => {
    // Check if user has already made a consent choice
    const hasConsent = consentManager.hasConsentChoice();
    if (!hasConsent) {
      // Small delay so it doesn't pop immediately on page load
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Subscribe to consent changes
  useEffect(() => {
    const unsubscribe = consentManager.subscribe((state) => {
      setConsentState(state);
      if (state.timestamp) {
        setVisible(false);
      }
    });
    return unsubscribe;
  }, []);

  const handleAcceptAll = () => {
    consentManager.grantAllConsent();
    setVisible(false);
  };

  const handleDeclineAll = () => {
    consentManager.denyAllConsent();
    setVisible(false);
  };

  const handleSavePreferences = () => {
    // Get current state and close
    setVisible(false);
  };

  const toggleCategory = (category: ConsentCategory) => {
    if (category === 'necessary') return; // Cannot toggle necessary
    
    if (consentState[category]) {
      consentManager.denyConsent([category]);
    } else {
      consentManager.grantConsent([category]);
    }
  };

  if (!visible) return null;

  const categories = [
    {
      id: 'necessary' as ConsentCategory,
      name: 'Necessary',
      description: 'Required for the website to function properly. These cannot be disabled.',
      icon: Shield,
      required: true,
      checked: true,
    },
    {
      id: 'analytics' as ConsentCategory,
      name: 'Analytics',
      description: 'Help us understand how visitors interact with our website by collecting anonymous data.',
      icon: BarChart3,
      required: false,
      checked: consentState.analytics,
    },
    {
      id: 'marketing' as ConsentCategory,
      name: 'Marketing',
      description: 'Used to deliver personalized advertisements and measure their effectiveness.',
      icon: Target,
      required: false,
      checked: consentState.marketing,
    },
    {
      id: 'personalization' as ConsentCategory,
      name: 'Personalization',
      description: 'Allow us to remember your preferences and provide a customized experience.',
      icon: Sparkles,
      required: false,
      checked: consentState.personalization,
    },
  ];

  return (
    <div
      className="fixed bottom-16 left-0 right-0 z-50 p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 shadow-2xl"
      role="dialog"
      aria-label="Cookie consent"
      aria-modal="true"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <Cookie className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
              We Value Your Privacy
            </h2>
            <p className="text-sm text-slate-700 dark:text-slate-300">
              We use cookies and similar technologies to enhance your experience, analyze site traffic, 
              and serve personalized content. You can choose which categories you want to allow. 
              Your choices will not affect your ability to use the site.
              See our{' '}
              <a href="/privacy" className="text-blue-600 dark:text-blue-400 hover:underline">
                Privacy Policy
              </a>{' '}
              and{' '}
              <a href="/terms" className="text-blue-600 dark:text-blue-400 hover:underline">
                Terms of Service
              </a>{' '}
              for details.
            </p>
          </div>
        </div>

        {/* Expandable Categories */}
        <div className="mb-4">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
            aria-expanded={expanded}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {expanded ? 'Hide Preferences' : 'Customize Preferences'}
          </button>

          {expanded && (
            <div className="mt-3 space-y-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              {categories.map((category) => {
                const Icon = category.icon;
                return (
                  <div
                    key={category.id}
                    className="flex items-start gap-3 p-3 bg-white dark:bg-slate-700 rounded-lg"
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      <Icon className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium text-slate-900 dark:text-white">
                          {category.name}
                          {category.required && (
                            <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                              (Required)
                            </span>
                          )}
                        </h3>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={category.checked}
                            onChange={() => toggleCategory(category.id)}
                            disabled={category.required}
                            className="sr-only peer"
                            aria-label={`Toggle ${category.name} cookies`}
                          />
                          <div
                            className={`w-11 h-6 rounded-full peer transition-colors ${
                              category.required
                                ? 'bg-blue-600 cursor-not-allowed'
                                : 'bg-slate-300 dark:bg-slate-600 peer-checked:bg-blue-600'
                            } peer-focus:ring-2 peer-focus:ring-blue-300`}
                          >
                            <div
                              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                                category.checked ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </div>
                        </label>
                      </div>
                      <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                        {category.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2">
          <button
            onClick={handleDeclineAll}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            Decline All
          </button>
          
          {expanded && (
            <button
              onClick={handleSavePreferences}
              className="px-4 py-2 text-sm font-medium text-white bg-slate-600 hover:bg-slate-700 rounded-lg transition-colors"
            >
              Save Preferences
            </button>
          )}
          
          <button
            onClick={handleAcceptAll}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            Accept All
          </button>
          
          <button
            onClick={handleDeclineAll}
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors sm:hidden"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Legal Notice */}
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400 text-center sm:text-right">
          By clicking "Accept All", you consent to the use of ALL cookies and tracking technologies.
          You can change your preferences at any time.
        </p>
      </div>
    </div>
  );
};

export default CookieConsent;
